import * as Blockly from 'blockly/core';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { FieldOperatorDropdown } from '@/(routers)/boards/editor/block/definitions/fields/fieldOperatorDropdown';
import { FieldStyledBadgeLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledBadgeLabel';

const LOOP_BLOCK_TYPES = new Set(['for', 'for_var', 'while', 'while_time']);
const ORANGE_ONLY_BLOCK_TYPES = new Set(['sdcard_write', 'sdcard_clear']);
const BLUE_ONLY_BLOCK_TYPES = new Set(['monitor_bluetooth']);

// FieldOperatorDropdown/FieldStyledBadgeLabelは独自描画のため、ブロック本体の網掛けとは別に
// 手動で反映する必要がある。setDisabledReasonを呼ぶ箇所は必ずこれも呼ぶこと。複数の独立した
// 無効化理由を持つブロックがあるため、ローカルのbooleanではなくisEnabled()の総合結果を見る。
export function syncOperatorDropdownDisabled(block: Blockly.Block): void {
    const disabled = !block.isEnabled();
    for (const input of block.inputList) {
        for (const field of input.fieldRow) {
            if (field instanceof FieldOperatorDropdown || field instanceof FieldStyledBadgeLabel) {
                field.setDisabled(disabled);
            }
        }
    }
}

export function validateFuncReturnBlock(returnBlock: Blockly.Block): void {
    const fnId: string = (returnBlock as any)._fnId ?? '';
    const parent = returnBlock.getParent();

    let shouldDisable = false;

    if (parent) {
        let parentFuncBlock: Blockly.Block | null = null;
        let cursor: Blockly.Block | null = parent;
        while (cursor) {
            if (cursor.type === 'func') {
                parentFuncBlock = cursor;
                break;
            }
            cursor = cursor.getParent();
        }
        if (!parentFuncBlock) {
            shouldDisable = true;
        } else {
            const parentFnId: string = (parentFuncBlock as any)._fnId ?? '';
            shouldDisable = fnId !== parentFnId;
        }
    }

    returnBlock.setDisabledReason(shouldDisable, 'func_mismatch');
    syncOperatorDropdownDisabled(returnBlock);
}

export function validateLoopControlBlock(block: Blockly.Block): void {
    const parent = block.getParent();

    if (!parent) {
        block.setDisabledReason(true, 'loop_mismatch');
        syncOperatorDropdownDisabled(block);
        return;
    }

    let foundLoop = false;
    let cursor: Blockly.Block | null = parent;
    while (cursor) {
        if (LOOP_BLOCK_TYPES.has(cursor.type)) {
            foundLoop = true;
            break;
        }
        cursor = cursor.getParent();
    }

    block.setDisabledReason(!foundLoop, 'loop_mismatch');
    syncOperatorDropdownDisabled(block);
}

export function validateBoardBlock(block: Blockly.Block, board: 'orange' | 'blue'): void {
    let disabled: boolean;
    if (ORANGE_ONLY_BLOCK_TYPES.has(block.type)) {
        disabled = board === 'blue';
    } else if (BLUE_ONLY_BLOCK_TYPES.has(block.type)) {
        const osType = useBoardEditorStore.getState().osType;
        disabled = board === 'orange' || osType === 'chromebook';
    } else {
        return;
    }
    block.setDisabledReason(disabled, 'board_mismatch');
    syncOperatorDropdownDisabled(block);
}

// monitor_bluetoothのBTデバイス名未取得時の使用不可判定。浮いている(getParent()が無い)だけの
// ブロックはコード生成に影響しないため対象外にする。
export function validateDeviceNameBlock(block: Blockly.Block): void {
    if (block.type !== 'monitor_bluetooth') return;
    const parent = block.getParent();
    const unresolved = !useBoardEditorStore.getState().boardIdentity?.bluetooth_device_name;
    block.setDisabledReason(!!parent && unresolved, 'device_name_unresolved');
    syncOperatorDropdownDisabled(block);
}

export function validateDeviceNameBlocksUnder(ws: Blockly.WorkspaceSvg, blockId: string): void {
    const movedBlock = ws.getBlockById(blockId);
    if (!movedBlock) return;
    const targets =
        movedBlock.type === 'monitor_bluetooth'
            ? [movedBlock]
            : movedBlock.getDescendants(false).filter((b) => b.type === 'monitor_bluetooth');
    for (const b of targets) {
        validateDeviceNameBlock(b);
    }
}

export function validateAllDeviceNameBlocks(ws: Blockly.WorkspaceSvg): void {
    for (const block of ws.getBlocksByType('monitor_bluetooth', false)) {
        validateDeviceNameBlock(block);
    }
}

export function validateFuncReturnBlocksUnder(ws: Blockly.WorkspaceSvg, blockId: string): void {
    const movedBlock = ws.getBlockById(blockId);
    if (!movedBlock) return;
    const targets =
        movedBlock.type === 'func_return'
            ? [movedBlock]
            : movedBlock.getDescendants(false).filter((b) => b.type === 'func_return');
    for (const returnBlock of targets) {
        validateFuncReturnBlock(returnBlock);
    }
}

export function validateLoopControlBlocksUnder(ws: Blockly.WorkspaceSvg, blockId: string): void {
    const movedBlock = ws.getBlockById(blockId);
    if (!movedBlock) return;
    const targets = ['break', 'continue'].includes(movedBlock.type)
        ? [movedBlock]
        : movedBlock.getDescendants(false).filter((b) => ['break', 'continue'].includes(b.type));
    for (const b of targets) {
        validateLoopControlBlock(b);
    }
}

export function validateBoardBlocksUnder(ws: Blockly.WorkspaceSvg, blockId: string): void {
    const movedBlock = ws.getBlockById(blockId);
    if (!movedBlock) return;
    const board = useBoardEditorStore.getState().board;
    const ALL_BOARD_SPECIFIC = new Set([...ORANGE_ONLY_BLOCK_TYPES, ...BLUE_ONLY_BLOCK_TYPES]);
    const targets = ALL_BOARD_SPECIFIC.has(movedBlock.type)
        ? [movedBlock]
        : movedBlock.getDescendants(false).filter((b) => ALL_BOARD_SPECIFIC.has(b.type));
    for (const b of targets) {
        validateBoardBlock(b, board);
    }
}

export function validateAllBoardBlocks(ws: Blockly.WorkspaceSvg, board: 'orange' | 'blue'): void {
    for (const block of ws.getAllBlocks(false)) {
        validateBoardBlock(block, board);
    }
}

const I2C_BLOCK_GROUPS: Record<string, Set<string>> = {
    oled: new Set(['oled_print', 'oled_clear', 'oled_bar']),
    barometric: new Set(['barometric_pressure']),
    accelerometer: new Set(['accelerometer', 'accelerometer_angle']),
};

function isInsideBoardOrFunc(block: Blockly.Block): boolean {
    let cursor: Blockly.Block | null = block.getParent();
    while (cursor) {
        if (cursor.type === 'schoomy_board' || cursor.type === 'func') return true;
        cursor = cursor.getParent();
    }
    return false;
}

function setI2CBlockDisabled(block: Blockly.Block, disabled: boolean): void {
    block.setDisabledReason(disabled, 'i2c_limit');
    syncOperatorDropdownDisabled(block);
}

export function validateI2CDevices(ws: Blockly.WorkspaceSvg): void {
    const activeGroupsUsed = new Set<string>();
    const activeI2CBlocks: Blockly.Block[] = [];
    const inactiveI2CBlocks: Blockly.Block[] = [];

    for (const [group, types] of Object.entries(I2C_BLOCK_GROUPS)) {
        for (const type of types) {
            for (const block of ws.getBlocksByType(type, false)) {
                if (isInsideBoardOrFunc(block)) {
                    activeGroupsUsed.add(group);
                    activeI2CBlocks.push(block);
                } else {
                    inactiveI2CBlocks.push(block);
                }
            }
        }
    }

    const tooMany = activeGroupsUsed.size > 2;

    for (const block of activeI2CBlocks) {
        setI2CBlockDisabled(block, tooMany);
    }
    for (const block of inactiveI2CBlocks) {
        setI2CBlockDisabled(block, false);
    }
}
